import React, { memo, useCallback } from 'react';
import { FilterDropdown } from '@rocco/ui/filter-sort/components/filters-bar/FilterDropdown';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FiltersConfigSection } from '@rocco/ui/filter-sort/types/filter-config-section';
import type { FilterConfigBarItem } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';
import { FiltersBarElement } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';
import { isFilterStateActive } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import { ScrollArea as RoccoScrollArea } from '@rocco/components/scroll-area';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';

/**
 * Reset a filter state to its initial/cleared state.
 * @param filterState The filter state to clear.
 * @returns The cleared filter state.
 */
const clearFilterState = (filterState: FilterState): FilterState => {
    // Check more specific types first (SearchableList has more properties than MultipleValues)
    if (isSearchableListFilterState(filterState)) {
        return {
            ...filterState,
            selectedItems: [],
        };
    }

    if (isMultipleValuesFilterState(filterState)) {
        return {
            ...filterState,
            items: filterState.items.map((item) => ({
                ...item,
                checked: false,
            })),
            selectedItems: [],
        };
    }

    if (isRangeFilterState(filterState)) {
        return {
            ...filterState,
            value: { ...filterState.range }, // Reset to full range
        };
    }

    if (isLocationFilterState(filterState)) {
        return {
            ...filterState,
            selectedLocation: null,
        };
    }

    if (isSingleValueFilterState(filterState)) {
        return { ...filterState, checked: false };
    }

    // Fallback: if filter has a 'checked' property, clear it directly
    // This handles edge cases where type guards might not match exactly
    const filterStateObj = filterState as unknown as Record<string, unknown>;
    if (
        'checked' in filterStateObj &&
        typeof filterStateObj.checked === 'boolean'
    ) {
        return { ...filterState, checked: false } as FilterState;
    }

    return filterState;
};

/** Memoized FiltersBar component to prevent unnecessary re-renders */
export const FiltersBar = memo(function FiltersBar() {
    const {
        searchFilters,
        popoverVariant,
        popoverBackground,
        showSelectedCountBadge,
        barTrailingComponent,
        hideFacetFilters,
        filterPillClassName,
    } = useFiltersContext();
    const { config, filterStates, updateFilter } = searchFilters;

    /**
     * Get the number of selected values for an individual filter state.
     * Used to display a per-section badge on filter pills (e.g. Brand, Colour).
     */
    const getSelectedValueCountForState = (
        state: FilterState | null,
    ): number => {
        if (!state) return 0;

        if (
            isSearchableListFilterState(state) ||
            isMultipleValuesFilterState(state)
        ) {
            return state.selectedItems.length;
        }

        if (isSingleValueFilterState(state)) {
            return state.checked ? 1 : 0;
        }

        // Range/location/etc aren't item-count based; treat as "1" when active.
        return isFilterStateActive(state) ? 1 : 0;
    };

    /**
     * Get the number of selected values across a filter section.
     */
    const getSelectedValueCountForSection = (
        filterSection: FiltersConfigSection,
        states: FilterStates,
    ): number => {
        return filterSection.filters.reduce((sum, filterConfigItem) => {
            const state = states.states[filterConfigItem.filterKey] ?? null;
            return sum + getSelectedValueCountForState(state);
        }, 0);
    };

    /**
     * Clear all filters in a section.
     * @param sectionFilters The filters in the section to clear.
     */
    const handleClearSection = useCallback(
        (sectionFilters: FilterConfigBarItem[]) => {
            const clearedStates = { ...filterStates.states };

            for (const filterConfigItem of sectionFilters) {
                const filterState = clearedStates[filterConfigItem.filterKey];
                if (filterState) {
                    clearedStates[filterConfigItem.filterKey] =
                        clearFilterState(filterState);
                }
            }

            updateFilter(
                {
                    ...filterStates,
                    states: clearedStates,
                },
                true, // refetch with cleared filters
            );
        },
        [filterStates, updateFilter],
    );

    /**
     * Toggle a facet item selection.
     * @param facetId The facet ID.
     * @param item The item to toggle.
     * @param checked Whether the item should be checked.
     */
    const handleToggleFacetItem = useCallback(
        (
            facetId: string,
            item: SingleValueFilterState<number>,
            checked: boolean,
        ) => {
            if (!filterStates.facet?.facetOptions) return;

            const updatedFacetOptions = filterStates.facet.facetOptions.map(
                (facet) => {
                    if (facet.id !== facetId) return facet;

                    const updatedItems = facet.state.items.map((i) =>
                        i.value === item.value ? { ...i, checked } : i,
                    );

                    const updatedSelectedItems = checked
                        ? [
                              ...facet.state.selectedItems,
                              { ...item, checked: true },
                          ]
                        : facet.state.selectedItems.filter(
                              (i) => i.value !== item.value,
                          );

                    return {
                        ...facet,
                        state: {
                            ...facet.state,
                            items: updatedItems,
                            selectedItems: updatedSelectedItems,
                        },
                    };
                },
            );

            updateFilter(
                {
                    ...filterStates,
                    facet: {
                        ...filterStates.facet,
                        facetOptions: updatedFacetOptions,
                    },
                },
                true,
            );
        },
        [filterStates, updateFilter],
    );

    /**
     * Clear all selections in a facet filter.
     * @param facetId The facet ID to clear.
     */
    const handleClearFacet = useCallback(
        (facetId: string) => {
            if (!filterStates.facet?.facetOptions) return;

            const updatedFacetOptions = filterStates.facet.facetOptions.map(
                (facet) => {
                    if (facet.id !== facetId) return facet;

                    return {
                        ...facet,
                        state: {
                            ...facet.state,
                            items: facet.state.items.map((i) => ({
                                ...i,
                                checked: false,
                            })),
                            selectedItems: [],
                        },
                    };
                },
            );

            updateFilter(
                {
                    ...filterStates,
                    facet: {
                        ...filterStates.facet,
                        facetOptions: updatedFacetOptions,
                    },
                },
                true,
            );
        },
        [filterStates, updateFilter],
    );

    return (
        <>
            {/* Static bar filters from config */}
            {config.barFilters.map(
                (filterSection) =>
                    isDropdownVisible(filterSection, filterStates) && (
                        <FilterDropdown
                            key={`filter-${filterSection.title}`}
                            title={filterSection.title}
                            selected={hasActiveFiltersInSection(
                                filterSection,
                                filterStates,
                            )}
                            badgeCount={
                                showSelectedCountBadge
                                    ? (() => {
                                          const count =
                                              getSelectedValueCountForSection(
                                                  filterSection,
                                                  filterStates,
                                              );
                                          return count > 0 ? count : undefined;
                                      })()
                                    : undefined
                            }
                            closeOnScroll={true}
                            popoverVariant={
                                filterSection.popoverVariant ?? popoverVariant
                            }
                            popoverBackground={
                                filterSection.popoverBackground ??
                                popoverBackground
                            }
                            popoverClassName={filterSection.popoverClassName}
                            onClear={() =>
                                handleClearSection(filterSection.filters)
                            }
                            triggerClassName={filterPillClassName}
                        >
                            <div
                                className="flex min-w-54 flex-col gap-2"
                                style={
                                    filterSection.minContentWidth
                                        ? {
                                              minWidth: `${filterSection.minContentWidth}rem`,
                                          }
                                        : undefined
                                }
                            >
                                {filterSection.filters.map(
                                    (filterConfigItem) => (
                                        <FiltersBarElement
                                            key={filterConfigItem.filterKey}
                                            filterConfigItem={filterConfigItem}
                                            filterState={
                                                filterStates.states[
                                                    filterConfigItem.filterKey
                                                ] ?? null
                                            }
                                        />
                                    ),
                                )}
                            </div>
                        </FilterDropdown>
                    ),
            )}

            {barTrailingComponent}

            {/* Dynamic facet filter pills (category-based filters) */}
            {!hideFacetFilters &&
                filterStates.facet?.facetOptions?.map(
                    (facetOption) =>
                        (facetOption.state.items.length > 0 ||
                            facetOption.state.selectedItems.length > 0) && (
                            <FilterDropdown
                                key={`facet-${facetOption.id}`}
                                title={facetOption.name}
                                selected={
                                    facetOption.state.selectedItems.length > 0
                                }
                                closeOnScroll={true}
                                popoverVariant={popoverVariant}
                                popoverBackground={popoverBackground}
                                onClear={() => handleClearFacet(facetOption.id)}
                                triggerClassName={filterPillClassName}
                            >
                                <FacetFilterContent
                                    facetId={facetOption.id}
                                    facetState={facetOption.state}
                                    onToggleItem={handleToggleFacetItem}
                                />
                            </FilterDropdown>
                        ),
                )}
        </>
    );
});

const isDropdownVisible = (
    filterSection: FiltersConfigSection,
    filterStates: FilterStates,
): boolean => {
    return filterSection.filters.some((filterConfigItem) => {
        const filterState =
            filterStates.states[filterConfigItem.filterKey] ?? null;

        if (filterState) {
            // Always show if filter is active (user selected it)
            // This ensures filters stay visible even when API returns 0 results
            if (isFilterStateActive(filterState)) {
                return true;
            }

            if (isLocationFilterState(filterState)) {
                return true;
            }

            if (isSingleValueFilterState(filterState)) {
                // Always show dropdowns, even when count is 0
                return true;
            }

            if (isSearchableListFilterState(filterState)) {
                const hasActiveSearch =
                    filterState.isSearching ||
                    filterState.search.trim().length > 0;

                return (
                    filterState.items.length > 0 ||
                    filterState.selectedItems.length > 0 ||
                    hasActiveSearch
                );
            }

            if (isMultipleValuesFilterState(filterState)) {
                return (
                    filterState.items.length > 0 ||
                    filterState.selectedItems.length > 0
                );
            }

            if (isRangeFilterState(filterState)) {
                const { min, max } = filterState.range;

                // Hide if range has no data or is invalid
                if (min === 0 && max === 0) {
                    return false;
                }
                if (min >= max) {
                    return false;
                }
                return true;
            }
        }

        return false;
    });
};

/**
 * Check if any filter in a filter section has active filters.
 * @param filterSection The filter section to check.
 * @param filterStates The current states of all filters.
 * @returns True if any filter in the section is active, false otherwise.
 */
const hasActiveFiltersInSection = (
    filterSection: FiltersConfigSection,
    filterStates: FilterStates,
): boolean => {
    return filterSection.filters.some((filterConfigItem) => {
        const filterState =
            filterStates.states[filterConfigItem.filterKey] ?? null;

        return filterState !== null && isFilterStateActive(filterState);
    });
};

/**
 * Props for FacetFilterContent component.
 */
interface FacetFilterContentProps {
    facetId: string;
    facetState: SearchableListFilterState<number>;
    onToggleItem: (
        facetId: string,
        item: SingleValueFilterState<number>,
        checked: boolean,
    ) => void;
}

/**
 * Content component for rendering facet filter items in a dropdown.
 * Similar to FiltersBarSearchableList but without aggregation/search functionality.
 */
const FacetFilterContent = ({
    facetId,
    facetState,
    onToggleItem,
}: FacetFilterContentProps) => {
    const scrollAreaItemHeight = 2.5;
    const scrollAreaHeight = facetState.items.length * scrollAreaItemHeight;
    const scrollAreaOverflown = scrollAreaHeight > 21;
    const scrollAreaStyles = {
        height: `${scrollAreaOverflown ? 21 : scrollAreaHeight}rem`,
    };

    // Show empty state message when no items available but there are selections
    if (facetState.items.length === 0 && facetState.selectedItems.length > 0) {
        return (
            <div
                className="flex min-w-54 flex-col gap-2"
                style={{ minWidth: '20rem' }}
            >
                <div className="px-2 py-3 text-sm text-gray-500">
                    No options available with current filters
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex min-w-54 flex-col gap-2"
            style={{ minWidth: '20rem' }}
        >
            <RoccoScrollArea
                style={scrollAreaStyles}
                scrollBarVisibility="hidden"
            >
                <div className="flex flex-col items-stretch gap-2">
                    {facetState.items.map((item) => (
                        <div
                            key={item.value}
                            className="group flex h-8 w-full cursor-pointer items-center rounded-2 px-2 hover:border-black hover:bg-primary-foreground hover:text-primary"
                            onClick={() => {
                                onToggleItem(facetId, item, !item.checked);
                            }}
                        >
                            <RoccoCheckbox
                                id={`facet-${facetId}-${item.value}`}
                                checked={item.checked}
                                groupHover="light"
                            />
                            <RoccoLabel target="checkbox">
                                {item.label}
                            </RoccoLabel>
                        </div>
                    ))}
                </div>
            </RoccoScrollArea>
        </div>
    );
};
